// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Override Reason Modal
// Clinical justification modal for DUR/PDMP overrides
// ============================================================================

import { useState } from 'react';
import { Button, Textarea } from '@/components/common/ui';

interface OverrideReasonModalProps {
  isOpen: boolean;
  title?: string;
  description?: string;
  alert?: { title?: string; description?: string };
  onClose: () => void;
  onSubmit: (reason: string) => void;
  isSubmitting: boolean;
}

export function OverrideReasonModal({
  isOpen,
  title = 'Override Required',
  description,
  alert,
  onClose,
  onSubmit,
  isSubmitting,
}: OverrideReasonModalProps) {
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  if (!isOpen) return null;

  const canSubmit = reason.trim().length >= 10 && acknowledged;

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit(reason.trim());
      setReason('');
      setAcknowledged(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setAcknowledged(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 animate-fade-in">
        <h2 className="text-xl font-bold text-slate-900 mb-2">{title}</h2>

        {description && (
          <p className="text-slate-600 mb-4">{description}</p>
        )}

        {alert && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4">
            {alert.title && (
              <div className="font-semibold text-amber-800">{alert.title}</div>
            )}
            {alert.description && (
              <div className="text-sm text-amber-700">{alert.description}</div>
            )}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Clinical Justification{' '}
            <span className="text-slate-400">(required, min 10 characters)</span>
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Enter clinical justification for this override..."
          />
          <div className="text-xs text-slate-500 mt-1">
            {reason.length}/10 characters minimum
          </div>
        </div>

        <div className="mb-6">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 rounded border-slate-300 text-pharmacy-primary focus:ring-pharmacy-primary"
            />
            <span className="text-sm text-slate-700">
              I acknowledge this override will be permanently recorded in the
              audit log and I take professional responsibility for this clinical
              decision.
            </span>
          </label>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
            className="flex-1"
          >
            Confirm Override
          </Button>
        </div>
      </div>
    </div>
  );
}
